import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronsUpDown, Loader2, MapPin, Network, Save, X, Zap } from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../../foundation/altzero_base/base/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../../foundation/altzero_base/base/components/ui/command';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../foundation/altzero_base/base/components/ui/dialog';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import {
  siteService, normaliseNmi, isEmbeddedNmi, lookupNmiAllocation, checkNmi, nmiBase,
  NMI_LENGTH, NMI_FULL_LENGTH,
} from '../services/siteService';
import { organisationService } from '../services/organisationService';
import { pickMlfForDate, pickDlfForDate, todayIso, type PickedFactor } from '../utils/lossFactorFy';
import { lossFactorService } from '../../scopingAi/services/lossFactorService';
import { lossFactorCodesService } from '../services/lossFactorCodesService';
import { CodeCombobox } from '../components/common/CodeCombobox';
import { uniqueByTariffCode } from '../utils/networkTariffs';
import { siteNetworkService, distributorsMatch } from '../services/siteNetworkService';
import type {
  BvSiteFormData, BvSiteStatus, BvOrganisation,
  BvSiteNetwork, BvSiteNetworkFormData,
} from '../types/billValidationTypes';

// ─── Network tariff form constants/helpers ─────────────────────────────────────

const EMPTY_NETWORK_FORM: BvSiteNetworkFormData = {
  tariff_code: '', tariff_name: '',
  distributor: '', description: '',
  name: '', channel: '', market_region: '', category: '',
  days_mapping_id: '', times_mapping_id: '', months_mapping_id: '',
  time_type_id: '', treatment_id: '',
  bucket_key: '', bill_category: '', demand_metric: '', interval_min: '',
  event_type: '', treatment_params: '', notes: '',
  open_to_new_applicants: true, price: '', price_type: '',
};

const EMPTY: BvSiteFormData = {
  nmi: '', client_id: '', sf_site_id: '',
  addr_street: '', addr_city: '', addr_state: '', addr_postcode: '',
  state: '', network_distributor: '',
  network_tariff_code: '', tni: '', dlf_code: '', dlf_override: '',
  mlf_code: '', mlf_override: '', network_tariff_start: '',
  market_status: 'Active', market_region: '', customer_classes: '',
  bill_val_status: 'Not Enrolled', enrolment_date: '', unenrolment_date: '',
};

const AU_STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'];

// Market region was a free-text field, which is how one production site ended up
// recording "Philippines". These are the values actually in use across sites and
// the network tariff rates.
const MARKET_REGIONS = [
  'AU-NEM.NSW', 'AU-NEM.VIC', 'AU-NEM.QLD', 'AU-NEM.SA', 'AU-NEM.ACT', 'AU-NEM.TAS',
];

type LossFactorLkState = {
  status: 'idle' | 'loading' | 'found' | 'notfound' | 'error';
  label?: string;
};

function LossFactorHint({ lk, notFound }: { lk: LossFactorLkState; notFound: string }) {
  if (lk.status === 'idle') return null;
  if (lk.status === 'loading') return <p className="mt-1 text-xs text-muted-foreground">Looking up…</p>;
  if (lk.status === 'error') return <p className="mt-1 text-xs text-red-600">{lk.label ?? 'Lookup failed'}</p>;
  if (lk.status === 'notfound') return <p className="mt-1 text-xs text-amber-600">{notFound}</p>;
  return <p className="mt-1 text-xs text-[#3790C5]">✓ Value populated{lk.label ? ` — ${lk.label}` : ''}</p>;
}

export function SiteFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState<BvSiteFormData>({
    ...EMPTY,
    client_id: searchParams.get('org') ?? '',
  });
  const [orgs, setOrgs] = useState<BvOrganisation[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'found' | 'not_found'>('idle');
  const [isEmbedded, setIsEmbedded] = useState(false);
  // NMI feedback: warning = accepted but the checksum digit disagrees,
  // error = wrong length, including a 10-character entry missing its checksum.
  const [nmiNotice, setNmiNotice] = useState<{ level: 'warn' | 'error'; message: string } | null>(null);

  // Network tariff codes
  const [distributors, setDistributors] = useState<string[]>([]);
  const [allNetworks, setAllNetworks] = useState<BvSiteNetwork[]>([]);
  const [assignedCodes, setAssignedCodes] = useState<string[]>([]);
  const [networksLoading, setNetworksLoading] = useState(false);
  const [showNewNetworkDialog, setShowNewNetworkDialog] = useState(false);
  const [networkComboOpen, setNetworkComboOpen] = useState(false);
  const [newNetworkForm, setNewNetworkForm] = useState<BvSiteNetworkFormData>(EMPTY_NETWORK_FORM);
  const [newNetworkSaving, setNewNetworkSaving] = useState(false);
  const [newNetworkError, setNewNetworkError] = useState<string | null>(null);

  // Loss-factor auto-fetch by code.
  const [mlfLk, setMlfLk] = useState<LossFactorLkState>({ status: 'idle' });
  const [dlfLk, setDlfLk] = useState<LossFactorLkState>({ status: 'idle' });
  // Codes loaded from the existing site — used to avoid overwriting a stored
  // value on mount (auto-fetch only kicks in once the code is changed).
  // What the code currently resolves to, shown read-only beside the code.
  const [resolvedMlf, setResolvedMlf] = useState<PickedFactor | null>(null);
  const [resolvedDlf, setResolvedDlf] = useState<PickedFactor | null>(null);

  const loadedMlfCode = useRef<string | null>(null);
  const loadedDlfCode = useRef<string | null>(null);
  // The NMI as stored. Some legacy/embedded rows hold non-conforming values
  // (e.g. 11-character alphanumeric test NMIs), so the length rule is enforced
  // on new and changed NMIs only — otherwise those sites become uneditable.
  const loadedNmi = useRef<string | null>(null);

  // Load orgs + all available tariff codes
  useEffect(() => {
    organisationService.list().then((r) => { if (r.success) setOrgs(r.data ?? []); });
    siteService.listDistributors().then((r) => { if (r.success) setDistributors(r.data ?? []); });
    setNetworksLoading(true);
    siteNetworkService.listAll()
      .then(r => { if (r.success) setAllNetworks(r.data ?? []); })
      .finally(() => setNetworksLoading(false));
  }, []);

  // Load existing site (edit only)
  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    siteService.getById(id).then((r) => {
      if (r.success && r.data) {
        const s = r.data;
        const bv = s.bv_extn;
        setIsEmbedded(bv?.is_embedded ?? false);
        loadedMlfCode.current = bv?.mlf_code || bv?.tni || '';
        loadedDlfCode.current = bv?.dlf_code ?? '';
        loadedNmi.current = normaliseNmi(s.nmi ?? '');
        setForm({
          nmi: s.nmi ?? '',
          client_id: s.client_id,
          sf_site_id: s.sf_site_id ?? '',
          addr_street: s.addr_street ?? '',
          addr_city: s.addr_city ?? '',
          addr_state: s.addr_state ?? '',
          addr_postcode: s.addr_postcode ?? '',
          state: bv?.state ?? '',
          network_distributor: bv?.network_distributor ?? '',
          network_tariff_code: bv?.network_tariff_code ?? '',
          tni: bv?.tni ?? '',
          dlf_code: bv?.dlf_code ?? '',
          dlf_override: bv?.dlf_override ?? '',
          mlf_code: bv?.mlf_code || bv?.tni || '',
          mlf_override: bv?.mlf_override ?? '',
          network_tariff_start: bv?.network_tariff_start ?? '',
          market_status: bv?.market_status ?? 'Active',
          market_region: bv?.market_region ?? '',
          customer_classes: (bv?.customer_classes ?? []).join(', '),
          bill_val_status: bv?.bill_val_status ?? 'Not Enrolled',
          enrolment_date: bv?.enrolment_date ?? '',
          unenrolment_date: bv?.unenrolment_date ?? '',
          network_reliability_value: bv?.network_reliability?.value ?? '',
          network_reliability_uom: bv?.network_reliability?.uom ?? '',
          specified_capacity_value: bv?.specified_capacity?.value ?? '',
          specified_capacity_uom: bv?.specified_capacity?.uom ?? '',
          cpd_demand_value: bv?.cpd_demand?.value ?? '',
          cpd_demand_uom: bv?.cpd_demand?.uom ?? '',
        });
        setAssignedCodes(s.network_codes ?? []);
      } else {
        setError(r.error ?? 'Failed to load site');
      }
      setLoading(false);
    });
  }, [isEdit, id]);

  const set = <K extends keyof BvSiteFormData>(k: K, v: BvSiteFormData[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // The MLF is keyed on the TNI, so choosing a TNI answers the MLF code too —
  // but only when it is still blank. Overwriting a code someone entered
  // deliberately would silently change the loss factor the comparison uses.
  const handleSelectTni = (code: string) =>
    setForm((f) => ({
      ...f,
      tni: code,
      mlf_code: String(f.mlf_code ?? '').trim() ? f.mlf_code : code,
    }));

  // Auto-fetch the MLF value when the MLF code is entered/changed. Skips the
  // value loaded from the site so a manual override is not clobbered on mount.
  useEffect(() => {
    const code = String(form.mlf_code ?? '').trim();
    if (!code) { setMlfLk({ status: 'idle' }); setResolvedMlf(null); return; }
    setMlfLk({ status: 'loading' });
    let active = true;
    const t = setTimeout(async () => {
      const res = await lossFactorService.getMlf(code);
      if (!active) return;
      if (!res.success) { setMlfLk({ status: 'error', label: res.error }); return; }
      const row = res.data?.[0];
      if (!row) { setMlfLk({ status: 'notfound' }); return; }
      // A site pin is not tied to one bill, so it resolves against today: the
      // financial year now in effect. pickMlfForDate is the single rule shared
      // with the site detail refresh, so the two cannot drift apart.
      const picked = pickMlfForDate(row, todayIso());
      if (picked.value === null) { setMlfLk({ status: 'notfound' }); return; }
      // Resolved for DISPLAY only — the value is never written to the site.
      // The code is the stored input; every consumer resolves the factor from
      // the loss factor table for the period it needs.
      setResolvedMlf(picked);
      const where = [row.location, row.region].filter(Boolean).join(' · ');
      setMlfLk({ status: 'found', label: `${where}${row.direction ? ` (${row.direction})` : ''} · ${picked.fy}` });
    }, 400);
    return () => { active = false; clearTimeout(t); };
  }, [form.mlf_code]);

  // Auto-fetch the DLF value when the DLF code is entered/changed.
  useEffect(() => {
    const code = String(form.dlf_code ?? '').trim();
    if (!code) { setDlfLk({ status: 'idle' }); setResolvedDlf(null); return; }
    setDlfLk({ status: 'loading' });
    let active = true;
    const t = setTimeout(async () => {
      const res = await lossFactorService.getDlf(code);
      if (!active) return;
      if (!res.success) { setDlfLk({ status: 'error', label: res.error }); return; }
      const row = res.data?.[0];
      if (!row) { setDlfLk({ status: 'notfound' }); return; }
      const picked = pickDlfForDate(row, todayIso());
      if (picked.value === null) { setDlfLk({ status: 'notfound' }); return; }
      setResolvedDlf(picked);
      const where = [row.dnsp, row.network_level ?? row.table_name].filter(Boolean).join(' · ');
      setDlfLk({ status: 'found', label: `${where} · ${picked.fy}` });
    }, 400);
    return () => { active = false; clearTimeout(t); };
  }, [form.dlf_code]);

  /** True when editing a site whose stored NMI is being kept as-is. */
  const isUnchangedNmi = (raw: string) =>
    isEdit && loadedNmi.current !== null && normaliseNmi(raw) === loadedNmi.current;

  const handleNmiBlur = async () => {
    if (!form.nmi) { setNmiNotice(null); return; }

    // The full 11-character NMI as printed on the bill is what gets stored —
    // imports already write it that way, and stripping the checksum here is what
    // used to leave form-created sites in a different format to imported ones.
    const check = checkNmi(form.nmi);
    const norm = check.nmi;
    if (isUnchangedNmi(form.nmi)) {
      setNmiNotice(check.kind === 'ok' ? null : {
        level: 'warn',
        message: `Stored NMI is not ${NMI_FULL_LENGTH} characters. Left as-is — change it to have it validated.`,
      });
      set('nmi', normaliseNmi(form.nmi));
      setIsEmbedded(isEmbeddedNmi(form.nmi));
      return;
    }
    if (check.kind === 'ok') {
      // A failed checksum is worth surfacing but must not block: embedded and
      // legacy NMIs do not all satisfy it.
      setNmiNotice(check.checksumValid ? null : {
        level: 'warn',
        message: `Final digit "${check.checksum}" is not the expected checksum for this NMI — `
          + 'check it against the bill.',
      });
    } else if (check.kind === 'missing_checksum') {
      setNmiNotice({
        level: 'error',
        message: check.expected != null
          ? `A NMI is ${NMI_FULL_LENGTH} characters as printed on the bill — this is ${NMI_LENGTH}. `
            + `The checksum digit for this NMI is "${check.expected}".`
          : `A NMI is ${NMI_FULL_LENGTH} characters as printed on the bill — this is ${NMI_LENGTH}.`,
      });
    } else {
      setNmiNotice({ level: 'error', message: check.message });
    }

    set('nmi', norm);
    setIsEmbedded(isEmbeddedNmi(norm));
    setLookupStatus('idle');
    // Distributor ranges are keyed on the 10-character identifier.
    if (norm.length < NMI_LENGTH) return;
    const alloc = await lookupNmiAllocation(nmiBase(norm));
    if (alloc) {
      setLookupStatus('found');
      if (!form.state) set('state', alloc.state);
      if (!form.network_distributor) set('network_distributor', alloc.distributor);
    } else {
      setLookupStatus('not_found');
    }
  };

  const handleSave = async () => {
    if (!form.nmi.trim()) { setError('NMI is required'); return; }
    if (!form.client_id) { setError('Entity is required'); return; }

    // Re-run the length check here: blur can be bypassed (paste + Enter, or the
    // Save button taking the focus straight from the field).
    const check = checkNmi(form.nmi);
    const grandfathered = isUnchangedNmi(form.nmi);
    if (!grandfathered && check.kind !== 'ok') {
      // A 10-character entry is rejected rather than padded: the checksum digit
      // belongs to the bill, and inventing it would produce a NMI that looks
      // complete while not matching the meter.
      const message = check.kind === 'missing_checksum'
        ? `A NMI is ${NMI_FULL_LENGTH} characters as printed on the bill — this is ${NMI_LENGTH}.`
          + (check.expected != null ? ` The checksum digit for this NMI is "${check.expected}".` : '')
        : check.message;
      setNmiNotice({ level: 'error', message });
      setError(message);
      return;
    }
    const nmi = grandfathered ? normaliseNmi(form.nmi) : check.nmi;

    setSaving(true);
    setError(null);

    // Network reliability: pass the form fields through verbatim. The
    // empty→absent and 0→rejected conversions happen in siteService + the
    // 068 CHECK constraint — the form must not synthesize { value: 0 } and
    // must not strip a cleared ('') field, which would make siteService.update
    // treat it as "untouched" and preserve the old value. Sending '' lets
    // siteService overwrite the stored key with undefined (absent); sending 0
    // lets the DB CHECK reject it with a readable message.
    const r = isEdit && id
      ? await siteService.update(id, { ...form, nmi, is_embedded: isEmbedded })
      : await siteService.create({ ...form, nmi, is_embedded: isEmbedded });
    if (!r.success || !r.data) {
      setError(r.error ?? 'Save failed');
      setSaving(false);
      return;
    }
    await siteNetworkService.updateSiteCodes(r.data.id, assignedCodes);
    navigate(`/bill-validation/sites/${r.data.id}`);
  };

  const handleCreateNetwork = async () => {
    if (!newNetworkForm.tariff_code.trim() || !newNetworkForm.distributor.trim()) return;
    setNewNetworkSaving(true);
    setNewNetworkError(null);
    const res = await siteNetworkService.create(newNetworkForm);
    if (res.success && res.data) {
      setAllNetworks(prev => [...prev, res.data!].sort((a, b) => a.tariff_code.localeCompare(b.tariff_code)));
      setAssignedCodes(prev => [...prev, res.data!.tariff_code]);
      setNewNetworkForm(EMPTY_NETWORK_FORM);
      setShowNewNetworkDialog(false);
    } else {
      setNewNetworkError(res.error ?? 'Failed to create tariff code');
    }
    setNewNetworkSaving(false);
  };

  if (loading) {
    return (
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-4">
        <div className="h-6 w-40 bg-gray-100 animate-pulse rounded" />
        <div className="h-96 bg-gray-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate(isEdit && id ? `/bill-validation/sites/${id}` : '/bill-validation/sites')}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {isEdit ? 'Back to Site' : 'Back to Sites'}
      </button>

      <div className="max-w-4xl space-y-6">

        {/* Header card */}
        <Card>
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 p-3 rounded-lg flex-shrink-0">
                <MapPin className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl md:text-3xl">
                  {isEdit ? 'Edit Site' : 'New Site'}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {isEdit
                    ? 'Update site details, network configuration, and enrolment status'
                    : 'Add a new NMI to an entity. Distributor auto-fills from the NMI range.'}
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* NMI + Entity */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Identification</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="site-nmi">NMI <span className="text-red-500">*</span></Label>
                <Input
                  id="site-nmi"
                  value={form.nmi}
                  onChange={(e) => { set('nmi', e.target.value); setLookupStatus('idle'); setNmiNotice(null); }}
                  onBlur={handleNmiBlur}
                  placeholder={`${NMI_FULL_LENGTH}-character NMI as printed on the bill`}
                  className={cn('mt-1.5 font-mono', nmiNotice?.level === 'error' && 'border-red-400 focus-visible:ring-red-400')}
                />
                {nmiNotice && (
                  <p className={cn('mt-1.5 text-xs', nmiNotice.level === 'error' ? 'text-red-600' : 'text-amber-600')}>
                    {nmiNotice.message}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1.5 min-h-[1.25rem]">
                  {lookupStatus === 'found' && (
                    <span className="text-xs text-green-600">✓ Distributor auto-filled from NMI range</span>
                  )}
                  {lookupStatus === 'not_found' && form.nmi && (
                    <span className="text-xs text-gray-400">No matching range — enter distributor manually</span>
                  )}
                  {isEmbedded && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-700">
                      Embedded Network
                    </span>
                  )}
                </div>
              </div>

              <div>
                <Label>Entity <span className="text-red-500">*</span></Label>
                <Select value={form.client_id} onValueChange={(v) => set('client_id', v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select entity…" /></SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {orgs.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1.5">
                    No active entities.{' '}
                    <button
                      onClick={() => navigate('/bill-validation/organisations/new')}
                      className="underline"
                    >Create one</button>
                  </p>
                )}
              </div>
            </div>

            {/* Salesforce IDs — manual entry so a site can be linked to SF
                without waiting on the bulk refresh to match it. */}
            <div>
              <Label htmlFor="site-sf-id">Salesforce Site ID</Label>
              <Input
                id="site-sf-id"
                value={form.sf_site_id ?? ''}
                onChange={(e) => set('sf_site_id', e.target.value.trim())}
                placeholder="e.g. a0X1x000000ABCDEAM"
                className="mt-1.5 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Salesforce Account ID is set on the entity, not the site.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Site Address</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label htmlFor="site-street">Street Address</Label>
              <Input
                id="site-street"
                value={form.addr_street ?? ''}
                onChange={(e) => set('addr_street', e.target.value)}
                placeholder="e.g. 123 Collins Street"
                className="mt-1.5"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <Label htmlFor="site-city">City / Suburb</Label>
                <Input
                  id="site-city"
                  value={form.addr_city ?? ''}
                  onChange={(e) => set('addr_city', e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>State</Label>
                <Select
                  value={form.addr_state ?? ''}
                  onValueChange={(v) => { set('addr_state', v); if (!form.state) set('state', v); }}
                >
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {AU_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="site-postcode">Postcode</Label>
                <Input
                  id="site-postcode"
                  value={form.addr_postcode ?? ''}
                  onChange={(e) => set('addr_postcode', e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Network Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Network Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>Distributor</Label>
                {/* Picked from the NMI allocation reference table rather than typed:
                    free text let typos through, and a misspelt distributor stops
                    tariff codes matching the site (distributorsMatch). Auto-fills
                    from the NMI range on blur. */}
                <Select
                  value={form.network_distributor ?? ''}
                  onValueChange={(v) => set('network_distributor', v)}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder={distributors.length ? 'Select distributor…' : 'Loading…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* A stored value that predates this list (or came from an
                        import) must still be selectable, not silently blanked. */}
                    {form.network_distributor && !distributors.includes(form.network_distributor) && (
                      <SelectItem value={form.network_distributor}>
                        {form.network_distributor} (not in reference list)
                      </SelectItem>
                    )}
                    {distributors.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Network State</Label>
                <Select
                  value={form.state ?? ''}
                  onValueChange={(v) => set('state', v)}
                >
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {AU_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Network reliability measure — site attribute (survives tariff change) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="network-reliability-value">Network Reliability Measure</Label>
                <Input
                  id="network-reliability-value"
                  type="number"
                  step="any"
                  min="0"
                  value={form.network_reliability_value ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') { set('network_reliability_value', ''); return; }
                    const parsed = parseFloat(raw);
                    set('network_reliability_value', Number.isFinite(parsed) ? parsed : '');
                  }}
                  placeholder="e.g. 2.5"
                  className="mt-1.5 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Level above which peak energy is chargeable for this connection. Leave empty if not applicable — an empty field saves as absent, never as zero.
                </p>
              </div>
              <div>
                <Label>Unit</Label>
                <Select
                  value={form.network_reliability_uom ?? ''}
                  onValueChange={(v) => set('network_reliability_uom', v as 'kW' | 'MW')}
                >
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kW">kW</SelectItem>
                    <SelectItem value="MW">MW</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Stored in the quoted unit; normalised to kW on read.
                </p>
              </div>
            </div>

            {/* Network Specified Capacity — the distributor's agreed capacity for
                the connection, priced per kVA per day by a tariff bucket with the
                "Fixed value configured on the site" treatment (e.g. AusNet NSP75/76). */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="specified-capacity-value">Network Specified Capacity</Label>
                <Input
                  id="specified-capacity-value"
                  type="number"
                  step="any"
                  min="0"
                  value={form.specified_capacity_value ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') { set('specified_capacity_value', ''); return; }
                    const parsed = parseFloat(raw);
                    set('specified_capacity_value', Number.isFinite(parsed) ? parsed : '');
                  }}
                  placeholder="e.g. 300"
                  className="mt-1.5 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The capacity on the network agreement, as printed on the invoice&apos;s Specified Capacity line.
                  Leave empty if the tariff has no specified capacity charge.
                </p>
              </div>
              <div>
                <Label>Unit</Label>
                <Select
                  value={form.specified_capacity_uom ?? ''}
                  onValueChange={(v) => set('specified_capacity_uom', v as 'kVA' | 'MVA')}
                >
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="kVA" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kVA">kVA</SelectItem>
                    <SelectItem value="MVA">MVA</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  MVA is converted to kVA when the bill is priced.
                </p>
              </div>
            </div>

            {/* AusNet Critical Peak Demand (job 99) — set yearly by AusNet from the
                nominated critical peak days, so it is entered from the invoice
                rather than measured; priced by the CRITICAL_PEAK_DEMAND bucket. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="cpd-demand-value">Critical Peak Demand (CPD)</Label>
                <Input
                  id="cpd-demand-value"
                  type="number"
                  step="any"
                  min="0"
                  value={form.cpd_demand_value ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') { set('cpd_demand_value', ''); return; }
                    const parsed = parseFloat(raw);
                    set('cpd_demand_value', Number.isFinite(parsed) ? parsed : '');
                  }}
                  placeholder="e.g. 120"
                  className="mt-1.5 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The CPD figure on the AusNet invoice (Critical Peak Demand line). Leave empty if the tariff has no CPD charge.
                </p>
              </div>
              <div>
                <Label>Unit</Label>
                <Select
                  value={form.cpd_demand_uom ?? ''}
                  onValueChange={(v) => set('cpd_demand_uom', v as 'kVA' | 'MVA')}
                >
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="kVA" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kVA">kVA</SelectItem>
                    <SelectItem value="MVA">MVA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>TNI</Label>
                {/* Job 27: chosen from transmission_loss_factor, and it fills the
                    MLF below — the TNI is what the MLF is keyed on. DLF Code used
                    to sit beside this, bound to the same field as the one in Loss
                    Factors; the duplicate is gone. */}
                <div className="mt-1.5">
                  <CodeCombobox
                    value={form.tni ?? ''}
                    onSelect={handleSelectTni}
                    search={lossFactorCodesService.searchTniCodes}
                    placeholder="Select TNI…"
                    searchPlaceholder="Search TNI codes…"
                    emptyText="No TNI codes match"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Sets the MLF code below if it is empty.
                </p>
              </div>
            </div>

            {/* ── Network Tariff Codes (multi-select) ── */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <Label className="flex items-center gap-1.5 text-sm font-medium">
                  <Network className="h-3.5 w-3.5 text-primary" />
                  Network Tariff Codes
                </Label>
                <div className="flex items-center gap-2">
                  <Popover open={networkComboOpen} onOpenChange={setNetworkComboOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 w-56 justify-between text-xs font-normal">
                        <span className="text-muted-foreground truncate">Assign existing code…</span>
                        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-0" align="end">
                      <Command>
                        <CommandInput placeholder="Search tariff codes…" className="h-9 text-xs" />
                        <CommandList>
                          <CommandEmpty>No codes found.</CommandEmpty>
                          <CommandGroup>
                            {/* One entry per code — the table holds one row per
                                bucket, so this listed each code many times (job 1). */}
                            {uniqueByTariffCode(allNetworks)
                              .filter(n =>
                                !assignedCodes.includes(n.tariff_code) &&
                                (!form.network_distributor || !n.distributor || distributorsMatch(n.distributor, form.network_distributor))
                              )
                              .map(n => (
                                <CommandItem
                                  key={n.id}
                                  value={n.tariff_code}
                                  onSelect={(val) => { setAssignedCodes(prev => [...prev, val]); setNetworkComboOpen(false); }}
                                >
                                  <span className="text-xs">{n.tariff_code}{n.distributor ? ` — ${n.distributor}` : ''}</span>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {networksLoading ? (
                <div className="flex items-center gap-2 py-3 text-xs text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading tariff codes…
                </div>
              ) : assignedCodes.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-6 text-center">
                  <Network className="h-6 w-6 text-gray-300 mx-auto mb-1.5" />
                  <p className="text-xs text-gray-400">
                    {allNetworks.length > 0
                      ? 'No tariff codes assigned — use the dropdown above'
                      : 'No tariff codes defined yet for your organisation'}
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100 text-xs text-muted-foreground uppercase tracking-wide">
                        <th className="px-4 py-2 text-left font-medium">Code</th>
                        <th className="px-4 py-2 text-left font-medium">Distributor</th>
                        <th className="px-4 py-2 w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {assignedCodes.map(code => {
                        const n = allNetworks.find(x => x.tariff_code === code);
                        return (
                          <tr key={code} className="hover:bg-gray-50/50">
                            <td className="px-4 py-2.5 font-mono font-semibold text-gray-900">{code}</td>
                            <td className="px-4 py-2.5 text-gray-500 text-xs">{n?.distributor ?? '—'}</td>
                            <td className="px-4 py-2.5">
                              <button
                                type="button"
                                title="Remove from site"
                                onClick={() => setAssignedCodes(prev => prev.filter(c => c !== code))}
                                className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Loss Factors */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Loss Factors</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>MLF Code</Label>
                {/* An MLF is keyed on the TNI, so the options are the TNI codes.
                    Selecting one leaves the existing effect to fill the value. */}
                <div className="mt-1.5">
                  <CodeCombobox
                    value={String(form.mlf_code ?? '')}
                    onSelect={(code) => set('mlf_code', code)}
                    search={lossFactorCodesService.searchTniCodes}
                    placeholder="Select MLF code…"
                    searchPlaceholder="Search MLF/TNI codes…"
                    emptyText="No MLF codes match"
                  />
                </div>
                <LossFactorHint lk={mlfLk} notFound="No MLF found for that code" />
              </div>
              <div>
                <Label htmlFor="mlf-override">MLF Value</Label>
                <p className="mt-1.5 text-sm font-mono text-gray-900">
                  {resolvedMlf?.value != null
                    ? `${resolvedMlf.value}${resolvedMlf.fy ? `  ·  FY ${resolvedMlf.fy}` : ''}`
                    : <span className="text-muted-foreground">resolved from the code</span>}
                </p>
                <Label htmlFor="mlf-override" className="mt-2 block text-xs text-muted-foreground">Override (leave blank to use the published value)</Label>
                <Input id="mlf-override" type="number" step="0.0001" value={form.mlf_override ?? ''} onChange={(e) => set('mlf_override', e.target.value === '' ? '' : parseFloat(e.target.value))} placeholder="none" className="mt-1 font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>DLF Code</Label>
                <div className="mt-1.5">
                  <CodeCombobox
                    value={String(form.dlf_code ?? '')}
                    onSelect={(code) => set('dlf_code', code)}
                    search={lossFactorCodesService.searchDlfCodes}
                    placeholder="Select DLF code…"
                    searchPlaceholder="Search DLF codes…"
                    emptyText="No DLF codes match"
                  />
                </div>
                <LossFactorHint lk={dlfLk} notFound="No DLF found for that code" />
              </div>
              <div>
                <Label htmlFor="dlf-override">DLF Value</Label>
                <p className="mt-1.5 text-sm font-mono text-gray-900">
                  {resolvedDlf?.value != null
                    ? `${resolvedDlf.value}${resolvedDlf.fy ? `  ·  FY ${resolvedDlf.fy}` : ''}`
                    : <span className="text-muted-foreground">resolved from the code</span>}
                </p>
                <Label htmlFor="dlf-override" className="mt-2 block text-xs text-muted-foreground">Override (leave blank to use the published value)</Label>
                <Input id="dlf-override" type="number" step="0.0001" value={form.dlf_override ?? ''} onChange={(e) => set('dlf_override', e.target.value === '' ? '' : parseFloat(e.target.value))} placeholder="none" className="mt-1 font-mono" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Loss Adjusted Rate = Rate × DLF × MLF. The site stores the CODE; the value is resolved from the published loss factor table for each bill's financial year. Set an override only to depart from the published value.</p>
          </CardContent>
        </Card>

        {/* Market Details */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Market Details</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>Market Status</Label>
                <Select value={form.market_status ?? 'Active'} onValueChange={(v) => set('market_status', v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Market Region</Label>
                <Select
                  value={form.market_region ?? ''}
                  onValueChange={(v) => set('market_region', v)}
                >
                  <SelectTrigger className="mt-1.5 font-mono">
                    <SelectValue placeholder="Select region…" />
                  </SelectTrigger>
                  <SelectContent>
                    {MARKET_REGIONS.map((r) => (
                      <SelectItem key={r} value={r} className="font-mono">{r}</SelectItem>
                    ))}
                    {/* A stored value outside the list still has to be selectable,
                        or editing an older site would silently rewrite its region. */}
                    {form.market_region && !MARKET_REGIONS.includes(form.market_region) && (
                      <SelectItem value={form.market_region} className="font-mono">
                        {form.market_region} (not a NEM region)
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="customer-classes">Customer Classes</Label>
              <Input id="customer-classes" value={form.customer_classes ?? ''} onChange={(e) => set('customer_classes', e.target.value)} placeholder="e.g. Large Business, Small Business" className="mt-1.5" />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated list</p>
            </div>
          </CardContent>
        </Card>

        {/* Enrolment */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Bill Validation Enrolment</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>Status</Label>
                <Select
                  value={form.bill_val_status ?? 'Not Enrolled'}
                  onValueChange={(v) => set('bill_val_status', v as BvSiteStatus)}
                >
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Not Enrolled', 'Pending Enrolment', 'Enrolled', 'Pending Cancellation', 'Cancelled'].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="enrol-date">Enrolment Date</Label>
                <Input id="enrol-date" type="date" value={form.enrolment_date ?? ''} onChange={(e) => set('enrolment_date', e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="unenrol-date">Unenrolment Date</Label>
                <Input id="unenrol-date" type="date" value={form.unenrolment_date ?? ''} onChange={(e) => set('unenrolment_date', e.target.value)} className="mt-1.5" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Errors */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pb-8">
          <Button
            variant="outline"
            onClick={() => navigate(isEdit && id ? `/bill-validation/sites/${id}` : '/bill-validation/sites')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Site'}
          </Button>
        </div>

      </div>

      {/* ── New Tariff Code dialog ── */}
      <Dialog open={showNewNetworkDialog} onOpenChange={open => { if (!open) { setNewNetworkError(null); setNewNetworkForm(EMPTY_NETWORK_FORM); } setShowNewNetworkDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Tariff Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="nn-code">Tariff Code <span className="text-red-500">*</span></Label>
                <Input
                  id="nn-code"
                  className="mt-1 font-mono uppercase"
                  placeholder="e.g. EA010"
                  value={newNetworkForm.tariff_code}
                  onChange={e => setNewNetworkForm(f => ({ ...f, tariff_code: e.target.value.toUpperCase() }))}
                />
              </div>
              <div>
                <Label htmlFor="nn-name">Tariff Name</Label>
                <Input
                  id="nn-name"
                  className="mt-1"
                  placeholder="e.g. Residential flat"
                  value={newNetworkForm.tariff_name}
                  onChange={e => setNewNetworkForm(f => ({ ...f, tariff_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="nn-dist">Distributor</Label>
                <Input
                  id="nn-dist"
                  className="mt-1"
                  placeholder="e.g. Ausgrid"
                  value={newNetworkForm.distributor}
                  onChange={e => setNewNetworkForm(f => ({ ...f, distributor: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="nn-desc">Description</Label>
              <Input
                id="nn-desc"
                className="mt-1"
                placeholder="Optional notes"
                value={newNetworkForm.description}
                onChange={e => setNewNetworkForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-1 border-t">Bucket</p>
            <div className="grid grid-cols-2 gap-3">
              {([
                { id: 'nn-bname', label: 'Name (bucket label)', field: 'name' },
                { id: 'nn-channel', label: 'Channel (E/B/K/Q/days)', field: 'channel' },
                { id: 'nn-mregion', label: 'Market Region', field: 'market_region' },
                { id: 'nn-category', label: 'Category', field: 'category' },
                { id: 'nn-price', label: 'Price', field: 'price', type: 'number' },
                { id: 'nn-ptype', label: 'Price Type (unit)', field: 'price_type' },
              ] as { id: string; label: string; field: keyof BvSiteNetworkFormData; type?: string }[]).map(({ id, label, field, type }) => (
                <div key={id}>
                  <Label htmlFor={id}>{label}</Label>
                  <Input
                    id={id}
                    type={type ?? 'text'}
                    step={type === 'number' ? 'any' : undefined}
                    className="mt-1"
                    placeholder="—"
                    value={String(newNetworkForm[field] ?? '')}
                    onChange={e => setNewNetworkForm(f => ({ ...f, [field]: e.target.value }))}
                  />
                </div>
              ))}

              <div className="col-span-2 space-y-1.5">
                <p className="text-xs text-muted-foreground">
                  Days, times, months and holidays are now managed in{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/admin')}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    Admin Portal → Network Tariffs
                  </button>
                  .
                </p>
              </div>

              <div className="col-span-2 flex items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={newNetworkForm.open_to_new_applicants}
                  onClick={() => setNewNetworkForm((f) => ({ ...f, open_to_new_applicants: !f.open_to_new_applicants }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${newNetworkForm.open_to_new_applicants ? 'bg-primary' : 'bg-muted'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${newNetworkForm.open_to_new_applicants ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm text-muted-foreground">
                  {newNetworkForm.open_to_new_applicants ? 'Open to new applicants' : 'Closed to new applicants'}
                </span>
              </div>
            </div>

            {newNetworkError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{newNetworkError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewNetworkDialog(false)} disabled={newNetworkSaving}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleCreateNetwork()}
              disabled={newNetworkSaving || !newNetworkForm.tariff_code.trim() || !newNetworkForm.distributor.trim()}
            >
              {newNetworkSaving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              {newNetworkSaving ? 'Creating…' : 'Create & Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
